<template>
  <div>
    <CCard>
      <CCardHeader>
        <strong>BGP 管理</strong>
        <small class="text-muted ml-2">instance(7.20+) / template / connection（RouterOS 7，自动适配版本差异）</small>
        <div class="float-right">
          <CButtonGroup>
            <CButton color="primary" size="sm" @click="openConnectionModal()">新建 Connection</CButton>
            <CButton color="success" size="sm" @click="openTemplateModal()">新建 Template</CButton>
            <CButton color="info" size="sm" @click="openInstanceModal()">新建 Instance</CButton>
            <CButton color="secondary" size="sm" :disabled="loading" @click="loadList">刷新</CButton>
          </CButtonGroup>
        </div>
      </CCardHeader>
      <CCardBody>
        <CAlert color="info" fade>
          BGP 创建顺序：<strong>Instance(仅 7.20+，提供 router-id/AS)</strong> → <strong>Template</strong> → <strong>Connection</strong>。
          7.15-7.19 版本无 instance 菜单，router-id 直接在 connection/template 上填写（系统自动适配）。
        </CAlert>

        <div v-for="g in regionGroups" :key="g.key" class="mb-3">
          <div class="bgp-region-header" @click="toggleRegion(g.key)">
            <strong>{{ g.name }}</strong>
            <CBadge color="dark" class="ml-2">{{ g.routers.length }} 台路由器</CBadge>
            <span class="ml-auto">
              <CButton color="light" size="sm">{{ expandedRegions.includes(g.key) ? '收起 ▲' : '展开 ▼' }}</CButton>
            </span>
          </div>
          <div v-if="expandedRegions.includes(g.key)" class="mt-2">
        <div v-for="r in g.routers" :key="r.router_id" class="mb-3">
          <div class="bgp-router-header" @click="toggleRouter(r.router_id)">
            <strong>{{ r.router_name }}</strong>
            <small class="text-muted ml-2">{{ r.router_host }}</small>
            <CBadge color="light" class="ml-2">{{ r.router_version }}</CBadge>
            <CBadge v-if="r.has_instance" color="dark" class="ml-2">7.20+ instance</CBadge>
            <CBadge color="info" class="ml-2">{{ r.instances.length }} ins / {{ r.templates.length }} tpl / {{ r.connections.length }} conn</CBadge>
            <span class="ml-auto">
              <CButtonGroup size="sm">
                <CButton color="dark" size="sm" @click.stop="openSessions(r)">Sessions</CButton>
                <CButton color="info" size="sm" @click.stop="openInstanceModal(null, r)">新建 Ins</CButton>
                <CButton color="success" size="sm" @click.stop="openTemplateModal(null, r)">新建 Tpl</CButton>
                <CButton color="primary" size="sm" @click.stop="openConnectionModal(null, r)">新建 Conn</CButton>
                <CButton color="light" size="sm">{{ expandedRouters.includes(r.router_id) ? '收起 ▲' : '展开 ▼' }}</CButton>
              </CButtonGroup>
            </span>
          </div>

          <div v-if="expandedRouters.includes(r.router_id)" class="bgp-router-body">
            <!-- Instances（仅 7.20+） -->
            <div v-if="r.has_instance" class="mb-3">
              <h6 class="mb-1">
                Instances
                <small class="text-muted ml-2">BGP 实例（router-id / AS / VRF 载体，被 connection 引用）</small>
              </h6>
              <table class="table table-sm mb-0">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Router ID</th>
                    <th>AS</th>
                    <th>Routing Table</th>
                    <th>VRF</th>
                    <th>状态</th>
                    <th width="200">操作</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="i in r.instances" :key="i.id" :class="{ 'text-muted': !i.enabled }">
                    <td><strong>{{ i.name }}</strong></td>
                    <td><code>{{ i.router_id_config || '-' }}</code></td>
                    <td>{{ i.as_num || '-' }}</td>
                    <td>{{ i.routing_table || 'main' }}</td>
                    <td>{{ i.vrf || '-' }}</td>
                    <td>
                      <CBadge :color="i.enabled ? 'success' : 'secondary'">{{ i.enabled ? '启用' : '禁用' }}</CBadge>
                    </td>
                    <td>
                      <CButtonGroup size="sm">
                        <CButton color="info" size="sm" @click="openInstanceModal(i, r)">编辑</CButton>
                        <CButton color="warning" size="sm" @click="toggleItem('instance', i)">{{ i.enabled ? '禁用' : '启用' }}</CButton>
                        <CButton color="danger" size="sm" @click="deleteItem('instance', i, r)">删除</CButton>
                      </CButtonGroup>
                    </td>
                  </tr>
                  <tr v-if="r.instances.length === 0">
                    <td colspan="7" class="text-center text-muted">暂无 instance</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Templates -->
            <div class="mb-3">
              <h6 class="mb-1">
                Templates
                <small class="text-muted ml-2">BGP 模板（会话通用参数，被 connection 引用）</small>
              </h6>
              <table class="table table-sm mb-0">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>AS</th>
                    <th>AFI</th>
                    <th v-if="!r.has_instance">Router ID</th>
                    <th>Filter (in / out)</th>
                    <th>Network</th>
                    <th>属性</th>
                    <th>状态</th>
                    <th width="200">操作</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="t in r.templates" :key="t.id" :class="{ 'text-muted': !t.enabled }">
                    <td><strong>{{ t.name }}</strong></td>
                    <td>{{ t.as_num || '-' }}</td>
                    <td>
                      <CBadge :color="t.afi === 'ipv6' ? 'dark' : 'light'">{{ t.afi || '-' }}</CBadge>
                    </td>
                    <td v-if="!r.has_instance"><code>{{ t.router_id_config || '-' }}</code></td>
                    <td><small>{{ t.input_filter || '-' }} / {{ t.output_filter_chain || '-' }}</small></td>
                    <td><small>{{ t.output_network || '-' }}</small></td>
                    <td>
                      <small>
                        <span v-if="t.output_keep_sent_attributes">keep-sent-attr </span>
                        <span v-if="t.output_remove_private_as">remove-private-as</span>
                        <span v-if="!t.output_keep_sent_attributes && !t.output_remove_private_as">-</span>
                      </small>
                    </td>
                    <td>
                      <CBadge :color="t.enabled ? 'success' : 'secondary'">{{ t.enabled ? '启用' : '禁用' }}</CBadge>
                    </td>
                    <td>
                      <CButtonGroup size="sm">
                        <CButton color="info" size="sm" @click="openTemplateModal(t, r)">编辑</CButton>
                        <CButton color="warning" size="sm" @click="toggleItem('template', t)">{{ t.enabled ? '禁用' : '启用' }}</CButton>
                        <CButton color="danger" size="sm" @click="deleteItem('template', t, r)">删除</CButton>
                      </CButtonGroup>
                    </td>
                  </tr>
                  <tr v-if="r.templates.length === 0">
                    <td :colspan="r.has_instance ? 8 : 9" class="text-center text-muted">暂无 template</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Connections -->
            <div>
              <h6 class="mb-1">
                Connections
                <small class="text-muted ml-2">BGP 连接（对端 + 引用 instance/template）</small>
              </h6>
              <table class="table table-sm mb-0">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Remote</th>
                    <th>AS</th>
                    <th>Local</th>
                    <th>Role</th>
                    <th>AFI</th>
                    <th>引用</th>
                    <th>状态</th>
                    <th width="200">操作</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="c in r.connections" :key="c.id" :class="{ 'text-muted': !c.enabled }">
                    <td><strong>{{ c.name }}</strong></td>
                    <td>
                      <div><code>{{ c.remote_address }}</code></div>
                      <small class="text-muted">
                        <CBadge v-if="c.remote_mode === 'router'" color="info">Router</CBadge>
                        <CBadge v-else-if="c.remote_mode === 'endpoint'" color="secondary">Endpoint</CBadge>
                        {{ c.remote_router_name || c.remote_endpoint_name || '' }}
                      </small>
                    </td>
                    <td>{{ c.remote_as_num }}</td>
                    <td><code>{{ c.local_address || '-' }}</code></td>
                    <td>{{ c.local_role || '-' }}</td>
                    <td>
                      <CBadge :color="c.afi === 'ipv6' ? 'dark' : 'light'">{{ c.afi || '-' }}</CBadge>
                    </td>
                    <td><small>{{ c.bgp_instance_name ? c.bgp_instance_name + ' / ' : '' }}{{ c.template_name }}</small></td>
                    <td>
                      <CBadge :color="c.enabled ? 'success' : 'secondary'">{{ c.enabled ? '启用' : '禁用' }}</CBadge>
                      <CBadge v-if="c.multihop" color="warning" class="ml-1">multihop</CBadge>
                    </td>
                    <td>
                      <CButtonGroup size="sm">
                        <CButton color="info" size="sm" @click="openConnectionModal(c, r)">编辑</CButton>
                        <CButton color="warning" size="sm" @click="toggleItem('connection', c)">{{ c.enabled ? '禁用' : '启用' }}</CButton>
                        <CButton color="danger" size="sm" @click="deleteItem('connection', c, r)">删除</CButton>
                      </CButtonGroup>
                    </td>
                  </tr>
                  <tr v-if="r.connections.length === 0">
                    <td colspan="9" class="text-center text-muted">暂无 connection</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
          </div>
        </div>
      </CCardBody>
    </CCard>

    <!-- Instance Modal -->
    <CModal :show.sync="instanceModal.show" :title="instanceModal.isEdit ? '编辑 BGP Instance' : '新建 BGP Instance'" color="info">
      <CForm>
        <CSelect
          v-if="!instanceModal.isEdit"
          label="路由器"
          v-model="instanceModal.form.router_id"
          :options="routerOptions"
          placeholder="请选择路由器"
          @change="onInstanceRouterChange"
        />
        <CInput label="Instance 名称" v-model="instanceModal.form.name" placeholder="如 bgp-instance-1" />
        <CRow>
          <CCol sm="6">
            <CSelect label="Router ID（从 IP 管理选择，必填）" v-model="instanceModal.form.router_id_config" :options="routerIdOptions" placeholder="请选择" />
          </CCol>
          <CCol sm="6">
            <CInput label="AS 号" v-model="instanceModal.form.as_num" placeholder="如 65011" />
          </CCol>
        </CRow>
        <CRow>
          <CCol sm="6">
            <CInput label="Routing Table" v-model="instanceModal.form.routing_table" placeholder="main" />
          </CCol>
          <CCol sm="6">
            <CInput label="VRF" v-model="instanceModal.form.vrf" placeholder="main" />
          </CCol>
        </CRow>
        <CAlert color="warning" fade>
          BGP Instance 仅 RouterOS 7.20+ 支持（低版本会提示不支持）。
        </CAlert>
      </CForm>
      <template #footer>
        <CButtonGroup>
          <CButton color="danger" @click="instanceModal.show = false">取消</CButton>
          <CButton color="success" :disabled="instanceModal.loading" @click="submitItem('instance')">
            {{ instanceModal.loading ? '保存中...' : '保存' }}
          </CButton>
        </CButtonGroup>
      </template>
    </CModal>

    <!-- Template Modal -->
    <CModal :show.sync="templateModal.show" :title="templateModal.isEdit ? '编辑 BGP Template' : '新建 BGP Template'" color="success" size="lg">
      <CForm>
        <CSelect
          v-if="!templateModal.isEdit"
          label="路由器"
          v-model="templateModal.form.router_id"
          :options="routerOptions"
          placeholder="请选择路由器"
          @change="onFormRouterChange('template')"
        />
        <CInput label="Template 名称" v-model="templateModal.form.name" placeholder="如 temp-as65011-internal-ipv4" />
        <CRow>
          <CCol sm="4">
            <CInput label="AS 号" v-model="templateModal.form.as_num" placeholder="如 65011" />
          </CCol>
          <CCol sm="4">
            <CSelect label="AFI" v-model="templateModal.form.afi" :options="[
              { value: 'ip', label: 'ip (IPv4)' },
              { value: 'ipv6', label: 'ipv6 (IPv6)' }
            ]" />
          </CCol>
          <CCol sm="4">
            <CInput label="Routing Table" v-model="templateModal.form.routing_table" placeholder="main" />
          </CCol>
        </CRow>
        <CSelect
          v-if="templateModal.caps && !templateModal.caps.has_instance"
          label="Router ID（低版本直接写在 template 上，从 IP 管理选择，必填）"
          v-model="templateModal.form.router_id_config"
          :options="routerIdOptions"
          placeholder="请选择"
        />
        <CRow>
          <CCol sm="4">
            <CSelect label="Input Filter" v-model="templateModal.form.input_filter" :options="templateChainOptions" placeholder="（不设置）" />
          </CCol>
          <CCol sm="4">
            <CSelect label="Output Filter Chain" v-model="templateModal.form.output_filter_chain" :options="templateChainOptions" placeholder="（不设置）" />
          </CCol>
          <CCol sm="4">
            <CSelect label="Output Filter Select" v-model="templateModal.form.output_filter_select" :options="templateChainOptions" placeholder="（不设置）" />
          </CCol>
        </CRow>
        <CSelect label="Output Network（地址列表名，通告网段来源，须已管理）" v-model="templateModal.form.output_network" :options="listNameOptions" placeholder="（不设置）" />
        <div class="form-group">
          <div>
            <CInputCheckbox
              label="Output Keep Sent Attributes"
              :checked="templateModal.form.output_keep_sent_attributes"
              @update:checked="v => templateModal.form.output_keep_sent_attributes = v"
              inline
            />
            <CInputCheckbox
              label="Output Remove Private AS"
              :checked="templateModal.form.output_remove_private_as"
              @update:checked="v => templateModal.form.output_remove_private_as = v"
              inline
            />
          </div>
        </div>
        <CAlert v-if="templateModal.caps" color="info" fade>
          目标路由器版本：{{ templateModal.caps.version }}
          <span v-if="templateModal.caps.has_instance">（router-id 在 instance 上，template 不支持）</span>
          <span v-else>（template 支持 router-id）</span>
        </CAlert>
      </CForm>
      <template #footer>
        <CButtonGroup>
          <CButton color="danger" @click="templateModal.show = false">取消</CButton>
          <CButton color="success" :disabled="templateModal.loading" @click="submitItem('template')">
            {{ templateModal.loading ? '保存中...' : '保存' }}
          </CButton>
        </CButtonGroup>
      </template>
    </CModal>

    <!-- Connection Modal -->
    <CModal :show.sync="connectionModal.show" :title="connectionModal.isEdit ? '编辑 BGP Connection' : '新建 BGP Connection'" color="primary" size="lg">
      <CForm>
        <CSelect
          v-if="!connectionModal.isEdit"
          label="路由器"
          v-model="connectionModal.form.router_id"
          :options="routerOptions"
          placeholder="请选择路由器"
          @change="onFormRouterChange('connection')"
        />
        <CInput label="Connection 名称" v-model="connectionModal.form.name" placeholder="如 bgp-peer-to-sz-core-bbr02-ipv4-backbone" />
        <CSelect label="对端类型" v-model="connectionModal.form.remote_mode" :options="[
          { value: 'router', label: '系统 Router（自动加载 IP / 复用 AS）' },
          { value: 'endpoint', label: '外部 AS 端点（在 BGP Endpoints 管理）' },
          { value: 'manual', label: '手动填写' }
        ]" />
        <CRow v-if="connectionModal.form.remote_mode === 'router'">
          <CCol sm="4">
            <CSelect
              label="对端路由器"
              v-model="connectionModal.form.remote_router_id"
              :options="routerOptions"
              placeholder="请选择"
              @change="onRemoteRouterChange()"
            />
          </CCol>
          <CCol sm="4">
            <CSelect
              label="对端地址（已管理 IP）"
              v-model="connectionModal.form.remote_address"
              :options="remoteIpOptions"
              placeholder="请选择"
            />
          </CCol>
          <CCol sm="4">
            <CSelect
              label="AS 来源（对端 instance/template）"
              v-model="connectionModal.form.as_source"
              :options="remoteAsSourceOptions"
              placeholder="请选择"
              @change="onAsSourceChange"
            />
          </CCol>
        </CRow>
        <CRow v-else-if="connectionModal.form.remote_mode === 'endpoint'">
          <CCol sm="4">
            <CSelect
              label="BGP 端点"
              v-model="connectionModal.form.remote_endpoint_id"
              :options="bgpEndpointOptions"
              placeholder="请选择"
              @change="onRemoteEndpointChange"
            />
          </CCol>
          <CCol sm="4">
            <CInput label="对端地址（来自端点）" :value="connectionModal.form.remote_address" disabled />
          </CCol>
          <CCol sm="4">
            <CInput label="Remote AS（来自端点）" :value="connectionModal.form.remote_as_num ? 'AS ' + connectionModal.form.remote_as_num : ''" disabled />
          </CCol>
        </CRow>
        <CRow v-else>
          <CCol sm="5">
            <CInput label="Remote Address（对端，可带 /32）" v-model="connectionModal.form.remote_address" placeholder="如 198.18.11.252/32" />
          </CCol>
        </CRow>
        <CRow>
          <CCol sm="4">
            <CInput
              v-if="connectionModal.form.remote_mode !== 'endpoint'"
              label="Remote AS"
              v-model="connectionModal.form.remote_as_num"
              placeholder="如 65011"
            />
          </CCol>
          <CCol sm="4">
            <CSelect label="Local Role" v-model="connectionModal.form.local_role" :options="[
              { value: 'ibgp', label: 'ibgp' },
              { value: 'ebgp', label: 'ebgp' },
              { value: 'ibgp-rr', label: 'ibgp-rr（RR 客户端/反射）' }
            ]" />
          </CCol>
          <CCol sm="4">
            <CSelect label="AFI" v-model="connectionModal.form.afi" :options="[
              { value: 'ip', label: 'ip (IPv4)' },
              { value: 'ipv6', label: 'ipv6 (IPv6)' }
            ]" />
          </CCol>
        </CRow>
        <CRow>
          <CCol sm="5">
            <CSelect
              label="Local Address（从本端 IP 管理选择）"
              v-model="connectionModal.form.local_address"
              :options="localAddressOptions"
              placeholder="请选择"
            />
          </CCol>
          <CCol sm="3">
            <CSelect label="Multihop" v-model="connectionModal.form.multihop" :options="[
              { value: 0, label: 'no' },
              { value: 1, label: 'yes' }
            ]" />
          </CCol>
        </CRow>

        <!-- 引用: 7.20+ 用 instance，低版本用 router-id + as -->
        <CRow v-if="connectionModal.caps && connectionModal.caps.has_instance">
          <CCol sm="6">
            <CSelect
              label="BGP Instance（7.20+ 必选）"
              v-model="connectionModal.form.bgp_instance_id"
              :options="instanceOptions(connectionModal.form.router_id)"
              placeholder="请选择 instance"
            />
          </CCol>
          <CCol sm="6">
            <CInput label="VRF" v-model="connectionModal.form.vrf" placeholder="main" />
          </CCol>
        </CRow>
        <CRow v-if="connectionModal.caps && !connectionModal.caps.has_instance">
          <CCol sm="6">
            <CSelect label="Router ID（低版本直接写在 connection 上，从 IP 管理选择，必填）" v-model="connectionModal.form.router_id_config" :options="routerIdOptions" placeholder="请选择" />
          </CCol>
          <CCol sm="6">
            <CInput label="本地 AS（覆盖 template 的 AS）" v-model="connectionModal.form.as_num" placeholder="如 65011" />
          </CCol>
        </CRow>

        <CRow>
          <CCol sm="6">
            <CSelect
              label="Template（必选，选择后带出其参数作为默认值）"
              v-model="connectionModal.form.template_id"
              :options="templateOptions(connectionModal.form.router_id)"
              placeholder="请选择 template"
              @change="onTemplateChange"
            />
          </CCol>
          <CCol sm="6">
            <CInput label="Routing Table" v-model="connectionModal.form.routing_table" placeholder="main" />
          </CCol>
        </CRow>
        <CRow>
          <CCol sm="4">
            <CSelect label="Input Filter" v-model="connectionModal.form.input_filter" :options="connectionChainOptions" placeholder="（不设置）" />
          </CCol>
          <CCol sm="4">
            <CSelect label="Output Filter Chain" v-model="connectionModal.form.output_filter_chain" :options="connectionChainOptions" placeholder="（不设置）" />
          </CCol>
          <CCol sm="4">
            <CSelect label="Output Filter Select" v-model="connectionModal.form.output_filter_select" :options="connectionChainOptions" placeholder="（不设置）" />
          </CCol>
        </CRow>
        <CSelect label="Output Network（地址列表名，通告网段来源，须已管理）" v-model="connectionModal.form.output_network" :options="listNameOptions" placeholder="（不设置）" />
        <div class="form-group">
          <div>
            <CInputCheckbox
              label="Output Keep Sent Attributes"
              :checked="connectionModal.form.output_keep_sent_attributes"
              @update:checked="v => connectionModal.form.output_keep_sent_attributes = v"
              inline
            />
            <CInputCheckbox
              label="Output Remove Private AS"
              :checked="connectionModal.form.output_remove_private_as"
              @update:checked="v => connectionModal.form.output_remove_private_as = v"
              inline
            />
          </div>
        </div>
        <CAlert v-if="connectionModal.caps" color="info" fade>
          目标路由器版本：{{ connectionModal.caps.version }}
          <span v-if="connectionModal.caps.has_instance">（引用 instance 提供 router-id/AS）</span>
          <span v-else>（router-id/AS 直接写在 connection 上）</span>
        </CAlert>
        <CAlert color="info" fade>
          Filter 只能选择系统中已管理的 Routing Filter Chain。Router ID/AS 下发位置由路由器版本决定，系统自动适配。
        </CAlert>
      </CForm>
      <template #footer>
        <CButtonGroup>
          <CButton color="danger" @click="connectionModal.show = false">取消</CButton>
          <CButton color="success" :disabled="connectionModal.loading" @click="submitItem('connection')">
            {{ connectionModal.loading ? '保存中...' : '保存' }}
          </CButton>
        </CButtonGroup>
      </template>
    </CModal>

    <!-- Sessions Modal -->
    <CModal :show.sync="sessionsModal.show" :title="'BGP Sessions - ' + sessionsModal.routerName" color="dark" size="xl">
      <div v-if="sessionsModal.loading" class="text-center text-muted my-3">拉取中...</div>
      <table v-else class="table table-sm">
        <thead>
          <tr>
            <th>Connection</th>
            <th>Instance</th>
            <th>Remote</th>
            <th>AS</th>
            <th>Local</th>
            <th>Role</th>
            <th>Established</th>
            <th>Uptime</th>
            <th>Prefixes</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(s, i) in sessionsModal.list" :key="i">
            <td>{{ s.name }}</td>
            <td>{{ s.instance }}</td>
            <td><code>{{ s.remote_address }}</code></td>
            <td>{{ s.remote_as }}</td>
            <td><small>{{ s.local_address }}</small></td>
            <td>{{ s.local_role }}</td>
            <td>
              <CBadge :color="s.established ? 'success' : 'danger'">
                {{ s.established ? 'Yes' : 'No' }}
              </CBadge>
            </td>
            <td>{{ s.uptime }}</td>
            <td>{{ s.prefix_count }}</td>
          </tr>
          <tr v-if="sessionsModal.list.length === 0">
            <td colspan="9" class="text-center text-muted">无 session</td>
          </tr>
        </tbody>
      </table>
      <template #footer>
        <CButtonGroup>
          <CButton color="secondary" @click="openSessions(sessionsModal.routerRef)">刷新</CButton>
          <CButton color="danger" @click="sessionsModal.show = false">关闭</CButton>
        </CButtonGroup>
      </template>
    </CModal>

    <!-- 通用确认 Modal -->
    <CModal :show.sync="confirmModal.show" :title="confirmModal.title" color="primary">
      <p>{{ confirmModal.message }}</p>
      <template #footer>
        <CButtonGroup>
          <CButton color="danger" @click="confirmModal.show = false">取消</CButton>
          <CButton color="success" @click="confirmModal.callback(); confirmModal.show = false">确认</CButton>
        </CButtonGroup>
      </template>
    </CModal>
  </div>
</template>

<script>
export default {
  name: 'BgpList',
  data() {
    return {
      loading: false,
      routers: [],
      allRouters: [],
      chainNames: [],
      templateChains: [],
      connectionChains: [],
      regions: [],
      expandedRegions: [],
      routerIps: [],
      routerListNames: [],
      remoteIps: [],
      remoteAsSources: [],
      bgpEndpoints: [],
      expandedRouters: [],
      instanceModal: {
        show: false, isEdit: false, editId: null, loading: false,
        form: this.emptyInstanceForm()
      },
      templateModal: {
        show: false, isEdit: false, editId: null, loading: false, caps: null,
        form: this.emptyTemplateForm()
      },
      connectionModal: {
        show: false, isEdit: false, editId: null, loading: false, caps: null,
        form: this.emptyConnectionForm()
      },
      sessionsModal: {
        show: false, loading: false, routerName: '', routerRef: null, list: []
      },
      confirmModal: {
        show: false, title: '确认', message: '', callback: () => {}
      }
    }
  },
  computed: {
    routerOptions() {
      return this.allRouters.map(r => ({ value: r.id, label: `${r.name} (${r.host})` }))
    },
    regionGroups() {
      // 按地域归类路由器（allRouters 提供 region_id, regions 提供名称）; 无地域 → 「未设置地域」排最后
      const map = {}
      this.regions.forEach(g => { map[g.id] = g })
      const groups = {}
      this.routers.forEach(r => {
        const rt = this.allRouters.find(a => a.id === r.router_id)
        const rid = (rt && rt.region_id !== null && rt.region_id !== undefined) ? rt.region_id : 'none'
        if (!groups[rid]) {
          groups[rid] = {
            key: rid,
            name: map[rid] ? map[rid].name : '未设置地域',
            order: rid === 'none' ? 99999 : rid,
            routers: []
          }
        }
        groups[rid].routers.push(r)
      })
      return Object.values(groups).sort((a, b) => a.order - b.order)
    },
    chainSelectOptions() {
      const opts = [{ value: '', label: '（不设置）' }]
      return opts.concat(this.chainNames.map(n => ({ value: n, label: n })))
    },
    templateChainOptions() {
      const opts = [{ value: '', label: '（不设置）' }]
      return opts.concat(this.templateChains.map(n => ({ value: n, label: n })))
    },
    connectionChainOptions() {
      const opts = [{ value: '', label: '（不设置）' }]
      return opts.concat(this.connectionChains.map(n => ({ value: n, label: n })))
    },
    routerIdOptions() {
      const opts = []
      this.routerIps.filter(i => i.family === 'ipv4').forEach(i => {
        const ip = (i.address || '').split('/')[0]
        if (ip) opts.push({ value: ip, label: `${ip} (${i.interface_name})` })
      })
      return opts
    },
    listNameOptions() {
      const opts = [{ value: '', label: '（不设置）' }]
      this.routerListNames.forEach(n => opts.push({ value: n, label: n }))
      return opts
    },
    remoteIpOptions() {
      return this.remoteIps.map(i => {
        const ip = (i.address || '').split('/')[0]
        return { value: ip, label: `${ip} (${i.interface_name}, ${i.family})` }
      })
    },
    localAddressOptions() {
      const wantFamily = this.connectionModal.form.afi === 'ipv6' ? 'ipv6' : 'ipv4'
      return this.routerIps
        .filter(i => i.family === wantFamily)
        .map(i => {
          const ip = (i.address || '').split('/')[0]
          return { value: ip, label: `${ip} (${i.interface_name})` }
        })
    },
    remoteAsSourceOptions() {
      return this.remoteAsSources.map(s => ({
        value: s.key, label: `${s.name} (AS ${s.as})`
      }))
    },
    bgpEndpointOptions() {
      return this.bgpEndpoints.map(e => ({
        value: e.id, label: `${e.name} / ${e.address} (AS ${e.as_num})`
      }))
    }
  },
  watch: {
    'connectionModal.form.afi'(newVal) {
      // AFI 切换后本端地址若与新地址族不匹配则清空
      const f = this.connectionModal.form
      if (!f.local_address) return
      const isV6 = f.local_address.includes(':')
      if ((newVal === 'ipv6') !== isV6) {
        f.local_address = ''
      }
    }
  },
  mounted() {
    this.loadList()
    this.loadAllRouters()
    this.loadChains()
    this.loadBgpEndpoints()
    this.loadRegions()
  },
  methods: {
    emptyInstanceForm() {
      return { router_id: '', name: '', router_id_config: '', as_num: '', routing_table: '', vrf: '' }
    },
    emptyTemplateForm() {
      return {
        router_id: '', name: '', as_num: '', afi: 'ip', routing_table: '',
        router_id_config: '', input_filter: '', output_filter_chain: '',
        output_filter_select: '', output_network: '',
        output_keep_sent_attributes: false, output_remove_private_as: false
      }
    },
    emptyConnectionForm() {
      return {
        router_id: '', name: '',
        remote_mode: 'router', remote_router_id: '', remote_endpoint_id: '',
        as_source: '', remote_address: '', remote_as_num: '',
        local_address: '', local_role: 'ibgp', afi: 'ip', multihop: 0,
        bgp_instance_id: '', template_id: '', router_id_config: '',
        as_num: '', vrf: '', routing_table: '',
        input_filter: '', output_filter_chain: '', output_filter_select: '',
        output_network: '', output_keep_sent_attributes: false, output_remove_private_as: false
      }
    },
    async loadAllRouters() {
      try {
        const res = await this.$axios.get('user/router')
        if (res.data.code === 200) {
          const own = res.data.userRouter || []
          const authed = (res.data.authedRouter || []).map(a => a.router).filter(Boolean)
          this.allRouters = [...own, ...authed]
        }
      } catch (e) { /* ignore */ }
    },
    async loadChains() {
      try {
        const res = await this.$axios.get('user/routing/filter/chains')
        if (res.data.code === 200) {
          this.chainNames = (res.data.data || []).map(c => c.name)
        }
      } catch (e) { /* ignore */ }
    },
    async loadRouterChains(kind, routerId) {
      // 该路由器上实际已下发的 chain（接口会连路由器实时查询 /routing/filter/rule + /routing/filterchain）
      const target = kind === 'template' ? 'templateChains' : 'connectionChains'
      if (!routerId) {
        this[target] = []
        return
      }
      try {
        const res = await this.$axios.get(`user/routing/filter/routers/${routerId}/chains`)
        if (res.data.code === 200) {
          this[target] = res.data.data || []
        }
      } catch (e) {
        this[target] = []
      }
    },
    async loadList() {
      this.loading = true
      try {
        const res = await this.$axios.get('user/bgp/list')
        if (res.data.code === 200) {
          this.routers = res.data.data || []
        } else {
          this.$toasted.show(res.data.msg || '加载失败', { type: 'error' })
        }
      } catch (e) {
        this.$toasted.show('网络错误: ' + e.message, { type: 'error' })
      }
      this.loading = false
    },
    async loadRegions() {
      try {
        const res = await this.$axios.get('user/region/list')
        if (res.data.code === 200) {
          this.regions = res.data.data || []
        }
      } catch (e) { /* ignore */ }
    },
    toggleRegion(key) {
      const i = this.expandedRegions.indexOf(key)
      if (i >= 0) this.expandedRegions.splice(i, 1)
      else this.expandedRegions.push(key)
    },
    toggleRouter(id) {
      const i = this.expandedRouters.indexOf(id)
      if (i >= 0) this.expandedRouters.splice(i, 1)
      else this.expandedRouters.push(id)
    },
    instanceOptions(routerId) {
      const r = this.routers.find(x => x.router_id === routerId)
      const list = r ? r.instances : []
      return list.map(i => ({ value: i.id, label: `${i.name} (AS ${i.as_num || '?'})` }))
    },
    templateOptions(routerId) {
      const r = this.routers.find(x => x.router_id === routerId)
      const list = r ? r.templates : []
      return list.map(t => ({ value: t.id, label: t.name }))
    },
    async loadRouterIps(routerId) {
      this.routerIps = []
      if (!routerId) return
      try {
        const res = await this.$axios.get('user/ip-address/list', { params: { router_id: routerId } })
        if (res.data.code === 200) {
          this.routerIps = res.data.ip_addresses || []
        }
      } catch (e) { /* ignore */ }
    },
    async loadRouterLists(routerId) {
      this.routerListNames = []
      if (!routerId) return
      try {
        const [alRes, rgRes] = await Promise.all([
          this.$axios.get('user/address-list/list', { params: { router_id: routerId } }),
          this.$axios.get('user/route-group/list')
        ])
        const names = []
        if (alRes.data.code === 200) {
          const r = (alRes.data.data || [])[0]
          ;(r ? r.list_names : []).forEach(n => { if (!names.includes(n)) names.push(n) })
        }
        // 路由组双身份的 list 名（组须关联该路由器）
        if (rgRes.data.code === 200) {
          ;(rgRes.data.data || []).forEach(g => {
            if (g.as_address_list && g.list_name && (g.routers || []).some(r => r.id === routerId)) {
              if (!names.includes(g.list_name)) names.push(g.list_name)
            }
          })
        }
        this.routerListNames = names
      } catch (e) { /* ignore */ }
    },
    async loadBgpEndpoints() {
      try {
        const res = await this.$axios.get('user/bgp/endpoint/list')
        if (res.data.code === 200) {
          this.bgpEndpoints = res.data.data || []
        }
      } catch (e) { /* ignore */ }
    },
    /* 对端路由器联动: 加载其已管理 IP + instance/template 的 AS 来源 */
    async onRemoteRouterChange(keepValues) {
      this.remoteIps = []
      this.remoteAsSources = []
      // keepValues=true: 编辑回显场景，保留已保存的对端地址（不清空）
      if (!keepValues) {
        this.connectionModal.form.remote_address = ''
        this.connectionModal.form.as_source = ''
      }
      const rid = this.connectionModal.form.remote_router_id
      if (!rid) return
      try {
        const [ipRes, bgpRes] = await Promise.all([
          this.$axios.get('user/ip-address/list', { params: { router_id: rid } }),
          this.$axios.get('user/bgp/list', { params: { router_id: rid } })
        ])
        if (ipRes.data.code === 200) {
          this.remoteIps = ipRes.data.ip_addresses || []
          // 已保存的对端地址不在已管理列表时补一项显示，避免回显空白
          if (keepValues && this.connectionModal.form.remote_address) {
            const cur = this.connectionModal.form.remote_address.split('/')[0]
            if (cur && !this.remoteIps.some(i => (i.address || '').split('/')[0] === cur)) {
              this.remoteIps.unshift({ address: cur + '/32', interface_name: '未纳管', family: '' })
            }
          }
        }
        if (bgpRes.data.code === 200) {
          const r = (bgpRes.data.data || [])[0]
          const sources = []
          ;(r ? r.instances : []).forEach(i => {
            if (i.as_num) sources.push({ key: 'ins:' + i.id, name: 'instance ' + i.name, as: i.as_num })
          })
          ;(r ? r.templates : []).forEach(t => {
            if (t.as_num) sources.push({ key: 'tpl:' + t.id, name: 'template ' + t.name, as: t.as_num })
          })
          this.remoteAsSources = sources
        }
      } catch (e) { /* ignore */ }
    },
    onAsSourceChange() {
      const s = this.remoteAsSources.find(x => x.key === this.connectionModal.form.as_source)
      if (s) {
        this.connectionModal.form.remote_as_num = String(s.as)
      }
    },
    onRemoteEndpointChange() {
      const e = this.bgpEndpoints.find(x => x.id === this.connectionModal.form.remote_endpoint_id)
      if (e) {
        this.connectionModal.form.remote_address = e.address
        this.connectionModal.form.remote_as_num = String(e.as_num)
      }
    },
    /* 选择 template 后带出其参数作为 connection 默认值（可修改覆盖） */
    onTemplateChange() {
      const r = this.routers.find(x => x.router_id === this.connectionModal.form.router_id)
      const t = r ? r.templates.find(x => x.id === this.connectionModal.form.template_id) : null
      if (!t) return
      const f = this.connectionModal.form
      if (t.afi) f.afi = t.afi
      if (t.as_num !== null && t.as_num !== undefined) f.as_num = String(t.as_num)
      if (t.input_filter) f.input_filter = t.input_filter
      if (t.output_filter_chain) f.output_filter_chain = t.output_filter_chain
      if (t.output_filter_select) f.output_filter_select = t.output_filter_select
      if (t.output_network) f.output_network = t.output_network
      f.output_keep_sent_attributes = !!t.output_keep_sent_attributes
      f.output_remove_private_as = !!t.output_remove_private_as
    },
    onInstanceRouterChange() {
      this.loadRouterIps(this.instanceModal.form.router_id)
    },
    async onFormRouterChange(kind) {
      const modal = kind === 'template' ? this.templateModal : this.connectionModal
      modal.caps = null
      this.loadRouterIps(modal.form.router_id)
      this.loadRouterLists(modal.form.router_id)
      this.loadRouterChains(kind, modal.form.router_id)
      if (!modal.form.router_id) return
      // 本地解析 router 表的 version 字段，不请求机器
      const r = this.allRouters.find(x => x.id === modal.form.router_id)
      if (!r || !r.version) {
        this.$toasted.show('该路由器版本未知（router 表无 version 记录）', { type: 'warning' })
        return
      }
      const m = r.version.match(/^(\d+)\.(\d+)/)
      if (!m) {
        this.$toasted.show('无法解析版本号: ' + r.version, { type: 'warning' })
        return
      }
      const major = parseInt(m[1], 10)
      const minor = parseInt(m[2], 10)
      modal.caps = {
        version: r.version,
        has_instance: major > 7 || (major === 7 && minor >= 20)
      }
    },
    showConfirm(title, message, callback) {
      this.confirmModal.title = title
      this.confirmModal.message = message
      this.confirmModal.callback = callback
      this.confirmModal.show = true
    },

    /* 三个 Modal */
    openInstanceModal(ins, routerRef) {
      if (ins) {
        this.loadRouterIps(ins.router_id)
        this.instanceModal.isEdit = true
        this.instanceModal.editId = ins.id
        this.instanceModal.form = {
          router_id: '', name: ins.name,
          router_id_config: ins.router_id_config || '',
          as_num: ins.as_num !== null && ins.as_num !== undefined ? String(ins.as_num) : '',
          routing_table: ins.routing_table || '', vrf: ins.vrf || ''
        }
      } else {
        this.instanceModal.isEdit = false
        this.instanceModal.editId = null
        this.instanceModal.form = this.emptyInstanceForm()
        if (routerRef) this.instanceModal.form.router_id = routerRef.router_id
        this.loadRouterIps(this.instanceModal.form.router_id)
      }
      this.instanceModal.show = true
    },
    openTemplateModal(tpl, routerRef) {
      if (tpl) {
        this.loadRouterIps(tpl.router_id)
        this.loadRouterLists(tpl.router_id)
        this.loadRouterChains('template', tpl.router_id)
        this.templateModal.isEdit = true
        this.templateModal.editId = tpl.id
        this.templateModal.caps = null
        const r = this.routers.find(x => x.router_id === tpl.router_id)
        if (r) this.templateModal.caps = { has_instance: r.has_instance, version: '' }
        this.templateModal.form = {
          router_id: tpl.router_id, name: tpl.name,
          as_num: tpl.as_num !== null && tpl.as_num !== undefined ? String(tpl.as_num) : '',
          afi: tpl.afi || 'ip',
          routing_table: tpl.routing_table || '',
          router_id_config: tpl.router_id_config || '',
          input_filter: tpl.input_filter || '',
          output_filter_chain: tpl.output_filter_chain || '',
          output_filter_select: tpl.output_filter_select || '',
          output_network: tpl.output_network || '',
          output_keep_sent_attributes: !!tpl.output_keep_sent_attributes,
          output_remove_private_as: !!tpl.output_remove_private_as
        }
      } else {
        this.templateModal.isEdit = false
        this.templateModal.editId = null
        this.templateModal.caps = null
        this.templateModal.form = this.emptyTemplateForm()
        if (routerRef) {
          this.templateModal.form.router_id = routerRef.router_id
          this.templateModal.caps = { has_instance: routerRef.has_instance, version: '' }
        }
        this.loadRouterIps(this.templateModal.form.router_id)
        this.loadRouterLists(this.templateModal.form.router_id)
        this.loadRouterChains('template', this.templateModal.form.router_id)
      }
      this.templateModal.show = true
    },
    openConnectionModal(c, routerRef) {
      if (c) {
        this.loadRouterIps(c.router_id)
        this.loadRouterLists(c.router_id)
        this.loadRouterChains('connection', c.router_id)
        this.connectionModal.isEdit = true
        this.connectionModal.editId = c.id
        this.connectionModal.caps = null
        const r = this.routers.find(x => x.router_id === c.router_id)
        if (r) this.connectionModal.caps = { has_instance: r.has_instance, version: '' }
        this.connectionModal.form = {
          router_id: c.router_id, name: c.name,
          remote_mode: c.remote_mode || 'manual',
          remote_router_id: c.remote_router_id || '',
          remote_endpoint_id: c.remote_endpoint_id || '',
          as_source: '',
          remote_address: c.remote_address,
          remote_as_num: String(c.remote_as_num),
          local_address: c.local_address || '',
          local_role: c.local_role || 'ibgp',
          afi: c.afi || 'ip',
          multihop: c.multihop ? 1 : 0,
          bgp_instance_id: c.bgp_instance_id || '',
          template_id: c.template_id || '',
          router_id_config: c.router_id_config || '',
          as_num: c.as_num !== null && c.as_num !== undefined ? String(c.as_num) : '',
          vrf: c.vrf || '', routing_table: c.routing_table || '',
          input_filter: c.input_filter || '',
          output_filter_chain: c.output_filter_chain || '',
          output_filter_select: c.output_filter_select || '',
          output_network: c.output_network || '',
          output_keep_sent_attributes: !!c.output_keep_sent_attributes,
          output_remove_private_as: !!c.output_remove_private_as
        }
        // 对端模式联动加载（编辑回显: 保留已保存值）
        this.loadBgpEndpoints()
        if (c.remote_mode === 'router' && c.remote_router_id) {
          this.onRemoteRouterChange(true)
        } else if (c.remote_mode === 'endpoint' && c.remote_endpoint_id) {
          this.onRemoteEndpointChange()
        } else {
          this.remoteIps = []
          this.remoteAsSources = []
        }
      } else {
        this.connectionModal.isEdit = false
        this.connectionModal.editId = null
        this.connectionModal.caps = null
        this.connectionModal.form = this.emptyConnectionForm()
        if (routerRef) {
          this.connectionModal.form.router_id = routerRef.router_id
          this.connectionModal.caps = { has_instance: routerRef.has_instance, version: '' }
        }
        this.loadRouterIps(this.connectionModal.form.router_id)
        this.loadRouterLists(this.connectionModal.form.router_id)
        this.loadRouterChains('connection', this.connectionModal.form.router_id)
      }
      this.connectionModal.show = true
    },

    /* 提交 */
    submitItem(kind) {
      if (kind === 'instance') this.submitInstance()
      else if (kind === 'template') this.submitTemplate()
      else this.submitConnection()
    },
    async submitInstance() {
      const f = this.instanceModal.form
      if (!this.instanceModal.isEdit && !f.router_id) {
        this.$toasted.show('请选择路由器', { type: 'error' })
        return
      }
      if (!f.name) {
        this.$toasted.show('请填写 Instance 名称', { type: 'error' })
        return
      }
      if (!f.router_id_config) {
        this.$toasted.show('请选择 Router ID', { type: 'error' })
        return
      }
      this.instanceModal.loading = true
      await this.doSubmit('instance', this.instanceModal)
      this.instanceModal.loading = false
    },
    async submitTemplate() {
      const f = this.templateModal.form
      if (!this.templateModal.isEdit && !f.router_id) {
        this.$toasted.show('请选择路由器', { type: 'error' })
        return
      }
      if (!f.name) {
        this.$toasted.show('请填写 Template 名称', { type: 'error' })
        return
      }
      if (this.templateModal.caps && !this.templateModal.caps.has_instance && !f.router_id_config) {
        this.$toasted.show('请选择 Router ID', { type: 'error' })
        return
      }
      this.templateModal.loading = true
      await this.doSubmit('template', this.templateModal)
      this.templateModal.loading = false
    },
    async submitConnection() {
      const f = this.connectionModal.form
      if (!this.connectionModal.isEdit && !f.router_id) {
        this.$toasted.show('请选择路由器', { type: 'error' })
        return
      }
      if (!f.name) {
        this.$toasted.show('请填写 Connection 名称', { type: 'error' })
        return
      }
      if (f.remote_mode === 'router') {
        if (!f.remote_router_id) {
          this.$toasted.show('请选择对端路由器', { type: 'error' })
          return
        }
        if (!f.remote_address) {
          this.$toasted.show('请选择对端地址（该路由器已管理 IP）', { type: 'error' })
          return
        }
      } else if (f.remote_mode === 'endpoint') {
        if (!f.remote_endpoint_id) {
          this.$toasted.show('请选择 BGP 端点', { type: 'error' })
          return
        }
      } else {
        if (!f.remote_address) {
          this.$toasted.show('请填写 Remote Address', { type: 'error' })
          return
        }
      }
      if (!f.remote_as_num) {
        this.$toasted.show('请填写 Remote AS（系统 Router 模式从 instance/template 复用）', { type: 'error' })
        return
      }
      if (!f.template_id) {
        this.$toasted.show('请选择 Template（须先创建）', { type: 'error' })
        return
      }
      if (this.connectionModal.caps && !this.connectionModal.caps.has_instance && !f.router_id_config) {
        this.$toasted.show('请选择 Router ID', { type: 'error' })
        return
      }
      this.connectionModal.loading = true
      await this.doSubmit('connection', this.connectionModal)
      this.connectionModal.loading = false
    },
    async doSubmit(kind, modal) {
      try {
        let res
        if (modal.isEdit) {
          res = await this.$axios.post(`user/bgp/${kind}/${modal.editId}/edit`, modal.form)
        } else {
          res = await this.$axios.post(`user/bgp/${kind}/add`, modal.form)
        }
        if (res.data.code === 200) {
          this.$toasted.show('已保存', { type: 'success' })
          modal.show = false
          this.loadList()
        } else {
          this.$toasted.show(res.data.msg || '保存失败', { type: 'error' })
        }
      } catch (e) {
        this.$toasted.show('网络错误: ' + e.message, { type: 'error' })
      }
    },

    /* toggle / delete */
    toggleItem(kind, item) {
      this.showConfirm('确认操作', `确认${item.enabled ? '禁用' : '启用'}该 ${kind} "${item.name}"？`, async () => {
        try {
          const res = await this.$axios.post(`user/bgp/${kind}/${item.id}/toggle`)
          if (res.data.code === 200) {
            this.$toasted.show('已切换', { type: 'success' })
            this.loadList()
          } else {
            this.$toasted.show(res.data.msg || '操作失败', { type: 'error' })
          }
        } catch (e) {
          this.$toasted.show('网络错误: ' + e.message, { type: 'error' })
        }
      })
    },
    deleteItem(kind, item, routerRef) {
      let extra = ''
      if (kind === 'instance') {
        const used = routerRef ? routerRef.connections.filter(c => c.bgp_instance_id === item.id).length : 0
        if (used > 0) {
          this.$toasted.show(`该 instance 被 ${used} 个 connection 引用，请先删除 connection`, { type: 'error' })
          return
        }
      }
      if (kind === 'template') {
        const used = routerRef ? routerRef.connections.filter(c => c.template_id === item.id).length : 0
        if (used > 0) {
          this.$toasted.show(`该 template 被 ${used} 个 connection 引用，请先删除 connection`, { type: 'error' })
          return
        }
      }
      this.showConfirm('确认删除', `确认删除 ${kind} "${item.name}"${extra}？路由器上的对应配置会一并移除。`, async () => {
        try {
          const res = await this.$axios.delete(`user/bgp/${kind}/${item.id}/delete`)
          if (res.data.code === 200) {
            this.$toasted.show('已删除', { type: 'success' })
            this.loadList()
          } else {
            this.$toasted.show(res.data.msg || '删除失败', { type: 'error' })
          }
        } catch (e) {
          this.$toasted.show('网络错误: ' + e.message, { type: 'error' })
        }
      })
    },

    /* Sessions */
    async openSessions(routerRef) {
      this.sessionsModal.routerRef = routerRef
      this.sessionsModal.routerName = routerRef.router_name
      this.sessionsModal.show = true
      this.sessionsModal.loading = true
      this.sessionsModal.list = []
      try {
        const res = await this.$axios.get(`user/bgp/router/${routerRef.router_id}/sessions`)
        if (res.data.code === 200) {
          this.sessionsModal.list = res.data.data || []
        } else {
          this.$toasted.show(res.data.msg || '拉取失败', { type: 'error' })
        }
      } catch (e) {
        this.$toasted.show('网络错误: ' + e.message, { type: 'error' })
      }
      this.sessionsModal.loading = false
    }
  }
}
</script>

<style scoped>
.bgp-region-header {
  display: flex;
  align-items: center;
  padding: 10px 12px;
  background: #4554;
  background: rgba(69, 85, 100, 0.12);
  border-left: 4px solid #45556e;
  border-radius: 4px;
  cursor: pointer;
  user-select: none;
}
.bgp-router-header {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  background: #e9ecef;
  border-radius: 4px;
  cursor: pointer;
  user-select: none;
}
.bgp-router-body {
  border: 1px solid #dee2e6;
  border-top: none;
  border-radius: 0 0 4px 4px;
  padding: 10px;
}
.ml-auto {
  margin-left: auto;
}
</style>
